"use client";

import Link from "next/link";
import { useMemo, type ReactNode } from "react";
import HeroCrossfade from "@/components/dashboard/jtbd/HeroCrossfade";
import BeforeAfterSlider from "./BeforeAfterSlider";
import {
  EDIT_CHAT_IMAGE,
  getHeroProducts,
  SELFIE_AFTER_HEADSHOT,
  SELFIE_BEFORE,
} from "./heroLabData";

function NewsCard({
  href,
  kicker,
  title,
  subtitle,
  children,
  overlayTitle,
}: {
  href: string;
  kicker: string;
  title: string;
  subtitle: string;
  children: ReactNode;
  overlayTitle?: string;
}) {
  return (
    <Link href={href} className="group flex w-[78vw] shrink-0 snap-start flex-col sm:w-auto">
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-gray-100">
        <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.03]">{children}</div>
        {overlayTitle ? (
          <span
            className="pointer-events-none absolute left-3 top-3 rounded-lg bg-white/90 px-2.5 py-1 text-xs font-bold text-gray-900 shadow-sm"
          >
            {overlayTitle}
          </span>
        ) : null}
      </div>
      <p className="mt-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-primary">{kicker}</p>
      <h3 className="mt-0.5 text-base font-bold leading-tight text-gray-900">{title}</h3>
      <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-gray-500">{subtitle}</p>
    </Link>
  );
}

/** Option C - slim headline + Aragon "What's New" row of four wide cards. */
export default function HeroWhatsNew() {
  const products = useMemo(() => getHeroProducts(), []);
  const viral = products["go-viral"].images.slice(0, 4);

  return (
    <section className="mb-8 md:mb-10">
      <h1
        className="text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl"
        style={{ fontFamily: "var(--font-merriweather)" }}
      >
        One selfie. Every version of you.
      </h1>
      <div className="mt-6 mb-3 flex items-end justify-between">
        <h2 className="text-lg font-bold text-gray-800 md:text-xl">What&apos;s new</h2>
      </div>
      <div
        className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4"
        style={{ scrollbarWidth: "none" }}
      >
        <NewsCard
          href={products["edit-chat"].href}
          kicker="New · Edit with Chat"
          title="Fix AI artifacts in one pass"
          subtitle="Plastic skin, warped hands, glitchy text after too many edits? Describe it and it's gone."
          overlayTitle="✨ Precise Edit"
        >
          <img src={EDIT_CHAT_IMAGE} alt="" className="h-full w-full object-cover object-center" />
        </NewsCard>
        <NewsCard
          href={products.photoshoot.href}
          kicker="New model"
          title="GPT Image 2.5 Flare"
          subtitle="Sharper, faster AI photoshoots for dating, work, and socials."
        >
          <HeroCrossfade images={products.photoshoot.images} imageClassName="inset-0 h-full w-full object-cover object-[center_25%]" />
        </NewsCard>
        <NewsCard
          href={products.headshots.href}
          kicker="Most popular"
          title="AI Headshots"
          subtitle="515+ studio looks from one selfie - drag to compare."
        >
          <BeforeAfterSlider
            before={SELFIE_BEFORE}
            after={SELFIE_AFTER_HEADSHOT}
            beforeLabel="Selfie"
            afterLabel="Headshot"
            className="h-full w-full"
          />
        </NewsCard>
        <NewsCard
          href={products["go-viral"].href}
          kicker="Trending"
          title="Go Viral styles"
          subtitle="The looks everyone is posting this week."
        >
          <div className="grid h-full w-full grid-cols-4 gap-0.5">
            {viral.map((src) => (
              <img key={src} src={src} alt="" className="h-full w-full object-cover object-top" loading="lazy" />
            ))}
          </div>
        </NewsCard>
      </div>
    </section>
  );
}
